import React, { useCallback } from 'react';
import { useTopLevelCategories } from '@retail/sanity/data-access';
import { CategoryListItem, Sparkles, TanstackQueryLoadingWrapper } from '@shared/components';
import { Box, ButtonBase, List, Stack, Typography } from '@mui/material';
import { CategoryState } from './CategoryState';
import { useConsumerType, useSanityLanguageCode } from '@retail/sanity/hooks';
import { useChatbotControls } from '@shared/hooks';
import { useSharedTFunction } from '@shared/i18n';
import { useHelpCenterTFunction } from '@retail/help-center/i18n';

interface Props {
  onCategoryClick: (category: CategoryState) => void;
  onClose: () => void;
}

export const TopLevelCategoriesContainer: React.FC<Props> = ({ onCategoryClick, onClose }) => {
  const t = useHelpCenterTFunction();
  const tShared = useSharedTFunction();
  const language = useSanityLanguageCode();
  const consumerType = useConsumerType();
  const loadingState = useTopLevelCategories({ language, consumer: consumerType });
  const { openChat } = useChatbotControls();
  const onOpenChat = useCallback(() => {
    openChat();
    onClose();
  }, [onClose, openChat]);

  return (
    <Stack>
      <Box component="section" sx={{ alignSelf: 'start', my: 2, mx: 3 }}>
        <Typography>{t('helpCenter.intro.title')}</Typography>
        <Stack direction="row" alignItems="center">
          <Typography sx={{ display: 'inline' }}>{t('helpCenter.intro.chatbotInfo')}</Typography>
          <Sparkles color="#ffdd00ff">
            <ButtonBase
              onClick={onOpenChat}
              sx={(theme) => ({
                color: 'primary.main',
                p: 1,
                fontWeight: 800,
                ...theme.applyStyles('dark', { color: 'primary.light' }),
              })}
            >
              {tShared('shared.chatbot.botName')}
            </ButtonBase>
          </Sparkles>
        </Stack>
      </Box>
      <Typography variant="h4" component="h2" sx={{ p: 2, pb: 0 }}>
        {t('helpCenter.categories')}
      </Typography>
      {/*@ts-ignore*/}
      <TanstackQueryLoadingWrapper loadingState={loadingState}>
        {(categories) => (
          <List>
            {categories.map((category) => (
              <CategoryListItem onClick={() => onCategoryClick(category)} key={category.id}>
                {category.title}
              </CategoryListItem>
            ))}
          </List>
        )}
      </TanstackQueryLoadingWrapper>
    </Stack>
  );
};
